/**
 * Contractor-module domain constants. Single source of truth for the
 * list page, the Add Contractor wizard, and the profile — mirrors the
 * backend enums on app\modules\api\models\Contractor.
 */

export const CONTRACTOR_STATUS = {
  INFORMATION_INCOMPLETE: "information_incomplete",
  PENDING_REVIEW: "pending_review",
  APPROVED: "approved",
  CONDITIONAL: "conditional",
  SUSPENDED: "suspended",
  EXPIRED: "expired",
};

export const CONTRACTOR_STATUS_META = {
  [CONTRACTOR_STATUS.INFORMATION_INCOMPLETE]: { label: "Information Incomplete", className: "status-incomplete" },
  [CONTRACTOR_STATUS.PENDING_REVIEW]: { label: "Pending Review", className: "status-pending" },
  [CONTRACTOR_STATUS.APPROVED]: { label: "Approved", className: "status-approved" },
  [CONTRACTOR_STATUS.CONDITIONAL]: { label: "Conditional", className: "status-conditional" },
  [CONTRACTOR_STATUS.SUSPENDED]: { label: "Suspended", className: "status-suspended" },
  [CONTRACTOR_STATUS.EXPIRED]: { label: "Expired", className: "status-expired" },
};

export const CONTRACTOR_RISK = {
  LOW: "low",
  MODERATE: "moderate",
  HIGH: "high",
  CRITICAL: "critical",
};

export const CONTRACTOR_RISK_META = {
  [CONTRACTOR_RISK.LOW]: {
    label: "Low",
    className: "risk-low",
    description: "Limited exposure, supervised, no hazardous plant",
  },
  [CONTRACTOR_RISK.MODERATE]: {
    label: "Moderate",
    className: "risk-moderate",
    description: "Operational work with manageable hazards",
  },
  [CONTRACTOR_RISK.HIGH]: {
    label: "High",
    className: "risk-high",
    description: "High-risk plant, hazardous substances or remote work",
  },
  [CONTRACTOR_RISK.CRITICAL]: {
    label: "Critical",
    className: "risk-critical",
    description: "Multiple fatal-risk hazards or major project",
  },
};

export const CONTRACTOR_TYPES = [
  { value: "company", label: "Company" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "trust", label: "Trust" },
];

export const CONTRACTOR_CATEGORIES = [
  "Electrical",
  "Plumbing",
  "Fencing",
  "Chemical Spraying",
  "Earthmoving",
  "Shearing",
  "Mustering",
  "Harvest",
  "Mechanical",
  "Building & Carpentry",
  "Pest Control",
  "Irrigation",
  "Transport & Livestock Carting",
  "Veterinary",
  "Agronomy",
  "Tree Work / Arborist",
  "Other",
];

export const ENGAGEMENT_TYPES = [
  { value: "contractor", label: "Contractor" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "labour_hire", label: "Labour Hire" },
  { value: "consultant", label: "Consultant" },
  { value: "supplier_with_site_access", label: "Supplier (with site access)" },
];

export const HAZARD_FLAGS = [
  { key: "mobile_plant_interaction", label: "Mobile plant interaction" },
  { key: "vehicle_interaction", label: "Vehicle interaction" },
  { key: "livestock_interaction", label: "Livestock interaction" },
  { key: "hazardous_chemical_exposure", label: "Hazardous chemical exposure" },
  { key: "electrical_work", label: "Electrical work" },
  { key: "working_at_heights", label: "Working at heights" },
  { key: "confined_space", label: "Confined space" },
  { key: "excavation", label: "Excavation" },
  { key: "hot_work", label: "Hot work" },
  { key: "remote_area_work", label: "Remote-area work" },
  { key: "water_or_dam_work", label: "Water or dam work" },
  { key: "tree_felling", label: "Tree-felling" },
  { key: "fire_risk", label: "Fire risk" },
  { key: "biosecurity_exposure", label: "Biosecurity exposure" },
  { key: "weather_exposure", label: "Weather exposure" },
  { key: "working_alone", label: "Working alone" },
];

export const SUPERVISION_LEVELS = [
  { value: "none", label: "None — works unsupervised" },
  { value: "periodic", label: "Periodic — check-ins during work" },
  { value: "high", label: "High — farm rep present for high-risk tasks" },
  { value: "constant", label: "Constant — farm rep present at all times" },
];

export const APPROVAL_AUTHORITIES = [
  { value: "property_owner", label: "Property Owner" },
  { value: "farm_manager", label: "Farm Manager" },
  { value: "safety_officer", label: "Safety Officer" },
];

export const CONTRACTOR_TABS = [
  { key: "all", label: "All Contractors" },
  { key: "approved", label: "Approved" },
  { key: "conditional", label: "Conditional" },
  { key: "expiring", label: "Expiring" },
  { key: "suspended_expired", label: "Suspended / Expired" },
];

/**
 * Profile tabs. `register` names the config key in
 * constants/contractorRegisters for tabs backed by a Phase 2 register;
 * overview/legal render bespoke content in the profile.
 */
export const PROFILE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "legal", label: "Legal & Contracts" },
  { key: "insurance", label: "Insurance", register: "insurance" },
  { key: "safety", label: "Safety", register: "safety" },
  { key: "personnel", label: "Personnel", register: "personnel" },
  { key: "assets", label: "Assets", register: "assets" },
  { key: "licences", label: "Licences", register: "licences" },
  { key: "inductions", label: "Inductions", register: "inductions" },
];

const AVATAR_COLORS = [
  "#16a34a", "#2563eb", "#ea580c", "#9333ea",
  "#0891b2", "#dc2626", "#ca8a04", "#4f46e5",
];

export const contractorAvatarColor = (name) => {
  const s = name || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const contractorInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/);
  if (!parts[0]) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};
